import React, { createContext, useContext, useEffect, useState } from "react";

const CartContext = createContext(null);
const CART_KEY = "dplus_cart";
const PROMO_KEY = "dplus_promo";

function readJSON(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch {
    return fallback;
  }
}

// Artwork previews are data URLs and can be large. The real files are already on
// our server (upload_id), so if the browser's storage is full we drop previews.
function stripPreviews(items) {
  return items.map((it) =>
    it.designPreview
      ? {
          ...it,
          designPreview: {
            ...it.designPreview,
            front: it.designPreview.front ? { ...it.designPreview.front, preview: null } : null,
            back: it.designPreview.back ? { ...it.designPreview.back, preview: null } : null,
          },
        }
      : it,
  );
}

function saveCart(items) {
  try {
    localStorage.setItem(CART_KEY, JSON.stringify(items));
  } catch {
    try {
      localStorage.setItem(CART_KEY, JSON.stringify(stripPreviews(items)));
    } catch {
      /* storage unavailable — cart still works for this visit */
    }
  }
}

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => readJSON(CART_KEY, []));
  // Promo code chosen in the cart; the server validates and applies it at checkout.
  const [promoCode, setPromoCodeState] = useState(() => readJSON(PROMO_KEY, null));

  useEffect(() => {
    saveCart(items);
  }, [items]);

  const setPromoCode = (code) => {
    setPromoCodeState(code || null);
    try {
      if (code) localStorage.setItem(PROMO_KEY, JSON.stringify(code));
      else localStorage.removeItem(PROMO_KEY);
    } catch {
      /* ignore */
    }
  };

  const addItem = (item) => {
    setItems((prev) => [...prev, { ...item, id: Date.now() + Math.random() }]);
  };
  const removeItem = (id) => setItems((prev) => prev.filter((p) => p.id !== id));
  const updateQty = (id, qty) =>
    setItems((prev) => prev.map((p) => (p.id === id ? { ...p, qty } : p)));
  // Checkout found a different server price for a catalog item: use the server price.
  const updatePrice = (id, unitCents) =>
    setItems((prev) =>
      prev.map((p) =>
        p.id === id
          ? { ...p, price: unitCents / 100, catalog: p.catalog ? { ...p.catalog, unit_price_cents: unitCents } : p.catalog }
          : p,
      ),
    );
  const clear = () => {
    setItems([]);
    setPromoCode(null);
  };

  const total = items.reduce((s, i) => s + (i.price || 0) * (i.qty || 1), 0);
  const count = items.reduce((s, i) => s + (i.qty || 1), 0);

  return (
    <CartContext.Provider
      value={{ items, addItem, removeItem, updateQty, updatePrice, clear, total, count, promoCode, setPromoCode }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used inside CartProvider");
  return ctx;
};
